import { Injectable } from '@angular/core';
import { intersectionBy, differenceBy } from 'lodash';
import { PeopleSearchType } from '../constants-types/Enums';
import { Person, PersonUpdate } from '../core-data-model/Person.model';
import { ContextDataService } from '../core-services/Context.dataservice';
import { SearchApi } from '../core-services/SearchApi.connect';
import { StringUtilities } from '../core-utilities/String.utility';

export enum EditState {
    Closed = 1,
    Open,
    CheckingDuplicates
}

@Injectable({ providedIn: 'root' })
export class SelectPeopleDataService {
    personEditing: Person = null;
    editState: EditState = EditState.Closed;

    constructor(
        private searchApi: SearchApi,
        private contextService: ContextDataService,
        private stringUtility: StringUtilities
    ) {}

    /**
     * Find all possible duplicate people that match a given person.
     *
     * @param person
     */
    async checkForDuplicatePeople(person: PersonUpdate): Promise<Person[]> {
        return Promise.all([
            this.getPeoplePossibilities(person.email),
            this.getPeoplePossibilities(person.fullName())
        ]).then(([emailMatches, nameMatches]) => {
            // Prioritize duplicates using this logic:
            // 1) Match on email AND name
            // 2) Match on email
            // 3) Match on name
            const doubleMatches = intersectionBy(
                emailMatches,
                nameMatches,
                (p) => p.id
            );
            const emailMatchesOnly = differenceBy(
                emailMatches,
                doubleMatches,
                (p) => p.id
            );
            const nameMatchesOnly = differenceBy(
                nameMatches,
                doubleMatches,
                (p) => p.id
            );
            return [...doubleMatches, ...emailMatchesOnly, ...nameMatchesOnly];
        });
    }

    getPeoplePossibilities(
        filterText: string,
        searchType?: PeopleSearchType
    ): Promise<Person[]> {
        if (!filterText || filterText.trim() === '') {
            return Promise.resolve([]);
        }
        return this.searchApi.getPersons(filterText, [], 5, searchType);
    }

    showNextButton() {
        return this.editState === EditState.Closed;
    }

    readyToGoOn() {
        return (
            this.contextService.selectedResultSetPeople.size > 0 &&
            this.contextService.selectedResultSetPeople.every(
                (person) =>
                    person.firstName &&
                    person.lastName &&
                    person.folder &&
                    this.stringUtility.isAnEmailString(person.email)
            )
        );
    }
}
